/**
 * CronPage.tsx — /app/cron — cron / heartbeat monitors
 *
 * Purpose:    List heartbeat monitors (GET /v1/cron), create new ones gated by
 *             the tier's heartbeat quota, show each job's ping URL, delete.
 * Inputs:     cronApi from @/lib/saas-telemetry; billingApi for tier
 * Outputs:    Heartbeat management UI
 * Constraints: Dark-first; WCAG AA. Gateway route rolling out — 404/405/501
 *              renders <ComingOnline/>, never a crash.
 * SOT:        nsentry SaaS completion spec § G-WEB (cron panel)
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { ComingOnline } from '@/components/ComingOnline'
import { cronApi, heartbeatPingUrl, type Heartbeat } from '@/lib/saas-telemetry'
import { billingApi } from '@/lib/saas-client'
import { TIERS, type TierId } from '@/lib/tiers'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

const STATUS_STYLES: Record<Heartbeat['status'], string> = {
  healthy: 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  late: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
  missing: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  pending: 'bg-brand-surface-2 text-brand-muted',
}

export function CronPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [beats, setBeats] = useState<Heartbeat[]>([])
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [intervalMinutes, setIntervalMinutes] = useState(60)
  const [formError, setFormError] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  async function load() {
    setState('loading')
    const [beatsRes, billingRes] = await Promise.all([cronApi.list(), billingApi.get()])
    if (!beatsRes.ok) {
      setError(beatsRes.error)
      setState('error')
      return
    }
    setBeats(beatsRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState(beatsRes.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  const quota = TIERS[tier].heartbeats
  const atLimit = beats.length >= quota

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    const result = await cronApi.create({ name, intervalSeconds: intervalMinutes * 60 })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setShowForm(false)
    setName('')
    await load()
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this heartbeat monitor?')) return
    await cronApi.remove(id)
    await load()
  }

  async function copyPingUrl(id: string) {
    await navigator.clipboard.writeText(heartbeatPingUrl(id))
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  if (state === 'error' && isComingOnline(error)) {
    return (
      <div className="p-6 lg:p-8">
        <h1 className="mb-6 text-xl font-semibold text-brand-ink">Cron / Heartbeats</h1>
        <ComingOnline feature="Heartbeat monitoring" description="Get alerted when a scheduled job stops checking in — curl a ping URL from your cron jobs." />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Cron / Heartbeats</h1>
          <p className="mt-1 text-sm text-brand-muted">
            {beats.length} / {quota} heartbeats ({TIERS[tier].label})
            {atLimit && (
              <>
                {' '}— <Link to="/app/billing" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">upgrade for more</Link>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          disabled={atLimit}
          className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add heartbeat
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Add heartbeat">
          {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="hb-name" className="mb-1 block text-sm font-medium text-brand-secondary">Name</label>
              <input id="hb-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="nightly-backup" className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none" />
            </div>
            <div>
              <label htmlFor="hb-interval" className="mb-1 block text-sm font-medium text-brand-secondary">Expected every</label>
              <select id="hb-interval" value={intervalMinutes} onChange={(e) => setIntervalMinutes(Number(e.target.value))} className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none">
                <option value={5}>5 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={60}>1 hour</option>
                <option value={360}>6 hours</option>
                <option value={1440}>24 hours</option>
                <option value={10080}>7 days</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">Create</button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2">Cancel</button>
          </div>
        </form>
      )}

      <AsyncScreen
        state={state}
        error={error}
        onRetry={load}
        emptyMessage="No heartbeats yet"
        emptyAction={
          <button type="button" onClick={() => setShowForm(true)} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500">
            Add your first heartbeat
          </button>
        }
      >
        <div className="space-y-3">
          {beats.map((hb) => (
            <div key={hb.id} className="rounded-xl border border-brand-subtle bg-brand-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[hb.status]}`}>{hb.status}</span>
                  <div>
                    <p className="font-medium text-brand-ink">{hb.name}</p>
                    <p className="text-xs text-brand-muted">
                      every {hb.intervalSeconds >= 3600 ? `${hb.intervalSeconds / 3600}h` : `${hb.intervalSeconds / 60}m`}
                      {hb.lastPingAt ? ` · last ping ${new Date(hb.lastPingAt).toLocaleString()}` : ' · never pinged'}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => handleDelete(hb.id)} className="text-xs text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                  Delete
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-brand-bg px-3 py-1.5 text-xs text-brand-secondary">POST {heartbeatPingUrl(hb.id)}</code>
                <button type="button" onClick={() => copyPingUrl(hb.id)} className="rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
                  {copiedId === hb.id ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </AsyncScreen>
    </div>
  )
}
